/**
 * 将 emits 对象的 key 转换为 onXxx 形式的监听器 prop 类型
 * 例如 change → onChange, visibleChange → onVisibleChange, update:modelValue → onUpdate:modelValue
 */
export type WithOnEmits<T> = {
  [K in keyof T & string as K extends `update:${infer Rest}`
    ? `onUpdate:${Rest}`
    : `on${Capitalize<K>}`]?: T[K];
};

/**
 * 剔除对象中的某些属性
 * @param obj
 * @param exclude
 */
export function excludeObjectKeys(
  obj?: Record<string, any>,
  exclude?: string[],
) {
  const tempObj: any = {};
  if (obj && exclude) {
    Object.keys(obj).forEach((key) => {
      if (!exclude.includes(key)) {
        tempObj[key] = obj[key];
      }
    });
  }
  return tempObj;
}

// 手动维护可以自动引入的组件
const CommonUIKey = [
  "IForm",
  "ISelect",
  "IRadio",
  "ICheckbox",
  "IDivider",
  "IDrawer",
  "IIconSelect",
  "IImageVerification",
  "ITextButton",
  "ILink",
  "IPageHeader",
  "ISearch",
  "ITable",
  "ISplitPane",
  "ITenantEnterprise",
  "ISegmented",
  "IDescriptions",
  "IDescriptionItem",
  "ILivePlayer",
  "IDialog",
  "IDatePicker",
];
/**
 * unplugin-auto-import/vite,unplugin-vue-components/vite 自动引入组件
 */
export function TzjCommonUIResolver(): {
  type: "component";
  resolve: (
    name: string,
  ) => { name: string; from: string; sideEffects: string } | undefined;
} {
  return {
    type: "component",
    resolve: (name: string) => {
      if (CommonUIKey.includes(name)) {
        // 修改样式文件名从驼峰命名法转换为短横线命名法
        const kebabCaseName = name
          .replace(/([a-zA-Z])([A-Z])/g, "$1-$2")
          .toLowerCase();
        return {
          name,
          from: "@tzj/common-ui",
          sideEffects: `@tzj/common-ui/theme-default/${kebabCaseName}.css`,
        };
      }
      return undefined; // 确保返回 undefined 当 name 不在 CommonUIKey 中
    },
  };
}

export class Logger {
  public _name: string;

  readonly nameStyle = "color:white;background-color:green;border-radius:2px;";
  readonly infoStyle = "color:gray";
  readonly warnStyle = "color:yellow";
  readonly errorStyle = "color:red";
  constructor(name: string) {
    this._name = name;
  }
  info(...args: any[]) {
    console.log(
      `%c ${this._name} `,
      this.nameStyle,
      "%c info ",
      this.infoStyle,
      ...args,
    );
  }
  warn(...args: any[]) {
    console.log(
      `%c ${this._name} `,
      this.nameStyle,
      "%c warn ",
      this.warnStyle,
      ...args,
    );
  }
  error(...args: any[]) {
    console.log(
      `%c ${this._name} `,
      this.nameStyle,
      "%c error ",
      this.errorStyle,
      ...args,
    );
  }
}

// 定义地图密钥配置的类型
interface MapKeyConfig {
  key: string;
  securityJsCode: string;
}

// 地图密钥列表
const keysList: MapKeyConfig[] = [
  {
    key: "38b0ca9fc864d48888c581df0fce884f",
    securityJsCode: "ace2949b84d7e74de5856f3c636e7601", // 新版key需要配合安全密钥
  },
  {
    key: "353ff229d1308165d356b2dda77073a0",
    securityJsCode: "e7694dbcaf1d66f689adf35d0508c3d7",
  },
  // {
  //   key: "fa8351b642f4f2399b7e4e88057f6769",
  //   securityJsCode: "47b08d4ba8807461dd047d470eef8d28",
  // },
];

/**
 * 随机获取一个地图密钥配置
 * @returns 地图密钥配置对象
 */
export function getMapKey(): MapKeyConfig {
  const randomIndex = Math.floor(Math.random() * keysList.length);
  const keyConfig = keysList?.[randomIndex];
  if (!keyConfig) {
    throw new Error("Failed to get map key configuration");
  }
  return keyConfig;
}

// 导出随机获取的地图密钥配置
export const mapKeyConfig: MapKeyConfig = getMapKey();
